/**
 * PokerVault Shipping Protection toggle — wires the cart's protection
 * switch to a real Shopify variant + matching cart attribute pair.
 *
 * Reads `window.PV.shippingProtection`:
 *   - variantId (number)              - PV-native protection product variant
 *   - attributesKey (string)          - default `_pv_shipping_protection`
 *
 * ON  → adds the variant (qty 1) + writes the cart attribute.
 * OFF → removes the variant (qty 0) + clears the cart attribute.
 *
 * If `variantId` is unset, no listeners are attached and a `console.warn`
 * is logged — graceful degradation when the shop metafield isn't seeded.
 *
 * Vanilla ES5 IIFE, no deps. Matches the style of `pokervault-upsell-guard.js`.
 */
(function () {
  'use strict';

  if (window.__PV_SHIPPING_PROTECTION_INIT__) return;
  window.__PV_SHIPPING_PROTECTION_INIT__ = true;

  var PV = window.PV || {};
  var cfg = PV.shippingProtection || {};
  var variantId = typeof cfg.variantId === 'number' ? cfg.variantId : 0;
  var ATTR_KEY =
    (typeof cfg.attributesKey === 'string' && cfg.attributesKey) || '_pv_shipping_protection';

  if (!variantId) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(
        '[pokervault-shipping-protection] window.PV.shippingProtection.variantId not set — toggle inert'
      );
    }
    return;
  }

  var SOURCE_ID = 'pv-shipping-protection';
  var inFlight = false;

  function toggleNodes() {
    return document.querySelectorAll('[data-pv-shipping-protection-toggle]');
  }

  function isOnFromAttributes(attrs) {
    return !!(attrs && attrs[ATTR_KEY] === 'true');
  }

  function syncChecked(checked) {
    var nodes = toggleNodes();
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].checked = checked;
      nodes[i].removeAttribute('disabled');
    }
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) {
        return r.text().then(function (text) {
          throw new Error('HTTP ' + r.status + ' on ' + url + ': ' + text);
        });
      }
      return r.json();
    });
  }

  function dispatchCartUpdate(cart) {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart || null,
            sourceId: SOURCE_ID,
            data: {
              source: SOURCE_ID,
              itemCount: cart && typeof cart.item_count === 'number' ? cart.item_count : 0,
            },
          },
        })
      );
    } catch (_e) {
      /* non-fatal */
    }
  }

  // Two calls because /cart/add.js doesn't accept cart-level `attributes`
  // (only line-item `properties`). /cart/update.js accepts both `updates`
  // and `attributes` but requires the line to exist first. The `updates`
  // clamp in step 2 also keeps qty=1 if another flow inflated it.
  function turnOn() {
    return postJson('/cart/add.js', { id: variantId, quantity: 1 }).then(function () {
      var updates = {};
      updates[String(variantId)] = 1;
      var attributes = {};
      attributes[ATTR_KEY] = 'true';
      return postJson('/cart/update.js', { updates: updates, attributes: attributes });
    });
  }

  function turnOff() {
    var updates = {};
    updates[String(variantId)] = 0;
    // Empty string clears a cart attribute per the Shopify Ajax cart spec.
    var attributes = {};
    attributes[ATTR_KEY] = '';
    return postJson('/cart/update.js', { updates: updates, attributes: attributes });
  }

  function onChange(ev) {
    var node = ev && ev.target;
    if (!node) return;

    // Re-entry guard: a second change while a flip is mid-fetch would race
    // the server-truth state. Snap the visual back and let the in-flight
    // call complete — its `.then` hydration will be the source of truth.
    if (inFlight) {
      node.checked = !node.checked;
      return;
    }

    var nextChecked = !!node.checked;
    var prevChecked = !nextChecked;
    inFlight = true;

    var promise = nextChecked ? turnOn() : turnOff();
    promise
      .then(function (cart) {
        syncChecked(nextChecked);
        dispatchCartUpdate(cart);
      })
      .catch(function (err) {
        if (typeof console !== 'undefined' && console.error) {
          console.error('[pokervault-shipping-protection] toggle failed', err);
        }
        // Revert the visual without re-firing change. No user-facing toast —
        // matches the rest of the theme's quiet-fail style for Ajax cart ops.
        syncChecked(prevChecked);
      })
      .then(function () {
        inFlight = false;
      });
  }

  function bindToggles() {
    var nodes = toggleNodes();
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      n.removeAttribute('disabled');
      // Per-node guard against double-binding when bindToggles is called
      // again after Horizon re-renders a cart section.
      if (n.__pvShipProtBound) continue;
      n.__pvShipProtBound = true;
      n.addEventListener('change', onChange);
    }
  }

  function hydrateFromCart(cart) {
    // While a user-initiated flip is in flight, the user's intent is the
    // source of truth — don't overwrite it from an interleaved cart fetch.
    if (inFlight) return;
    if (!cart || typeof cart !== 'object') return;
    syncChecked(isOnFromAttributes(cart.attributes));
  }

  function initFromCartJs() {
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (cart) {
        if (cart) hydrateFromCart(cart);
      })
      .catch(function () {
        /* non-fatal — Liquid's server-rendered `checked` stays in place */
      });
  }

  function onCartUpdateEvent(e) {
    // Skip our own dispatches — we already synced UI in the promise `.then`.
    if (e && e.detail && e.detail.sourceId === SOURCE_ID) return;

    // Horizon re-renders the cart drawer + main cart on every cart:update;
    // re-bind so toggles in freshly-rendered DOM get listeners attached.
    bindToggles();

    var cart = e && e.detail && e.detail.resource;
    if (cart && cart.attributes) {
      hydrateFromCart(cart);
    } else {
      initFromCartJs();
    }
  }

  function boot() {
    bindToggles();
    initFromCartJs();
    document.addEventListener('cart:update', onCartUpdateEvent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
